// auto-generated by Typewriter template _models.tst from PortCall.EndPoints\Api\Coordination\ApiCoordination.cs
import S, { DataSignal } from 's-js';
import SArray, { SDataArray } from 's-array';
import TZDate from 'lib/TZDate';
import { jsonableObservable } from 'lib/jsonableObservable';
import { CoordinationAltStatusLabel, CoordinationAltStatusLabelData, CoordinationAltStatusLabelOptions } from 'data/models/CoordinationAltStatusLabel';
import { CoordinationCard, CoordinationCardData, CoordinationCardOptions } from 'data/models/CoordinationCard';
import { CoordinationLocation, CoordinationLocationData, CoordinationLocationOptions } from 'data/models/CoordinationLocation';
import { CoordinationMilestoneType, CoordinationMilestoneTypeData, CoordinationMilestoneTypeOptions } from 'data/models/CoordinationMilestoneType';
import { CoordinationProvider, CoordinationProviderData, CoordinationProviderOptions } from 'data/models/CoordinationProvider';
import { Domain, DomainData, DomainOptions } from 'data/models/Domain';
import { CoordinationCurrency } from 'server/enums/CoordinationCurrency';

// CoordinationData is the class that Coordination serializes to/from
export interface CoordinationData {
    Id : number;
    Url : string | null;
	
    AltStatusLabels : CoordinationAltStatusLabelData[] | null;
    Cards : CoordinationCardData[] | null;
    Domain : DomainData | null;
    DomainId : number;
    Locations : CoordinationLocationData[] | null;
    MilestoneTypes : CoordinationMilestoneTypeData[] | null;
    Providers : CoordinationProviderData[] | null;
	
    AdminNotes : string | null;
    AgentPrompt : string | null;
    AllowsMultiple : boolean;
    AppearsOnTraffic : boolean;
    Currency : CoordinationCurrency;
    Description : string;
    DisplayOrder : number;
    IsMandatory : boolean;
    IsScopedByMainLocation : boolean;
    IsScopedByRelatedLocation : boolean;
    Title : string;
}

// CoordinationOptions is the class which may be passed to the Coordination constructor to define optional initial data values
export interface CoordinationOptions {
    Id : number;
    Url? : string | null;
	
    AltStatusLabels? : CoordinationAltStatusLabelOptions[] | null;
    Cards? : CoordinationCardOptions[] | null;
    Domain? : DomainOptions | null;
    DomainId? : number;
    Locations? : CoordinationLocationOptions[] | null;
    MilestoneTypes? : CoordinationMilestoneTypeOptions[] | null;
    Providers? : CoordinationProviderOptions[] | null;
	
    AdminNotes? : string | null;
    AgentPrompt? : string | null;
    AllowsMultiple? : boolean;
    AppearsOnTraffic? : boolean;
    Currency? : CoordinationCurrency;
    Description? : string;
    DisplayOrder? : number;
    IsMandatory? : boolean;
    IsScopedByMainLocation? : boolean;
    IsScopedByRelatedLocation? : boolean;
    Title? : string;
}

export class Coordination {
    public Id : DataSignal<number>;
    public Url : DataSignal<string | null>;
	
    public AltStatusLabels : SDataArray<CoordinationAltStatusLabel>;
    public Cards : SDataArray<CoordinationCard>;
    public Domain : DataSignal<Domain | null>;
    public DomainId : DataSignal<number>;
    public Locations : SDataArray<CoordinationLocation>;
    public MilestoneTypes : SDataArray<CoordinationMilestoneType>;
    public Providers : SDataArray<CoordinationProvider>;
	
    public AdminNotes : DataSignal<string | null>;
    public AgentPrompt : DataSignal<string | null>;
    public AllowsMultiple : DataSignal<boolean>;
    public AppearsOnTraffic : DataSignal<boolean>;
    public Currency : DataSignal<CoordinationCurrency>;
    public Description : DataSignal<string>;
    public DisplayOrder : DataSignal<number>;
    public IsMandatory : DataSignal<boolean>;
    public IsScopedByMainLocation : DataSignal<boolean>;
    public IsScopedByRelatedLocation : DataSignal<boolean>;
    public Title : DataSignal<string>;

    constructor();
    constructor(data : CoordinationOptions);
    constructor(data: CoordinationOptions | null = null) {
        this.Id = jsonableObservable(S.value<number>(data && data.Id !== undefined ? data.Id : 0));
        this.Url = jsonableObservable(S.value<string | null>(data && data.Url !== undefined ? data.Url : null));
	    
        this.AltStatusLabels = SArray<CoordinationAltStatusLabel>([]);
        this.Cards = SArray<CoordinationCard>([]);
        this.Domain = S.value<Domain | null>(null);
        this.DomainId = jsonableObservable(S.value<number>(data && data.DomainId !== undefined ? data.DomainId : 0));
        this.Locations = SArray<CoordinationLocation>([]);
        this.MilestoneTypes = SArray<CoordinationMilestoneType>([]);
        this.Providers = SArray<CoordinationProvider>([]);
	    
        this.AdminNotes = jsonableObservable(S.value<string | null>(data && data.AdminNotes !== undefined ? data.AdminNotes : null));
        this.AgentPrompt = jsonableObservable(S.value<string | null>(data && data.AgentPrompt !== undefined ? data.AgentPrompt : null));
        this.AllowsMultiple = jsonableObservable(S.value<boolean>(data && data.AllowsMultiple !== undefined ? data.AllowsMultiple : false));
        this.AppearsOnTraffic = jsonableObservable(S.value<boolean>(data && data.AppearsOnTraffic !== undefined ? data.AppearsOnTraffic : false));
        this.Currency = jsonableObservable(S.value<CoordinationCurrency>(data && data.Currency !== undefined ? data.Currency : 0));
        this.Description = jsonableObservable(S.value<string>(data && data.Description !== undefined ? data.Description : ''));
        this.DisplayOrder = jsonableObservable(S.value<number>(data && data.DisplayOrder !== undefined ? data.DisplayOrder : 0));
        this.IsMandatory = jsonableObservable(S.value<boolean>(data && data.IsMandatory !== undefined ? data.IsMandatory : false));
        this.IsScopedByMainLocation = jsonableObservable(S.value<boolean>(data && data.IsScopedByMainLocation !== undefined ? data.IsScopedByMainLocation : false));
        this.IsScopedByRelatedLocation = jsonableObservable(S.value<boolean>(data && data.IsScopedByRelatedLocation !== undefined ? data.IsScopedByRelatedLocation : false));
        this.Title = jsonableObservable(S.value<string>(data && data.Title !== undefined ? data.Title : ''));
    }

    static key(obj : Coordination) {
        return obj.Id();
    }
    
    static dataKey(obj : CoordinationOptions) {
        return obj.Id;
    }

    static update(obj : Coordination, data : CoordinationOptions) {
        S.freeze(() => {
            if (data.Id !== undefined) obj.Id(data.Id);
            if (data.Url !== undefined) obj.Url(data.Url);
	        
            if (data.DomainId !== undefined) obj.DomainId(data.DomainId);
	        
            if (data.AdminNotes !== undefined) obj.AdminNotes(data.AdminNotes);
            if (data.AgentPrompt !== undefined) obj.AgentPrompt(data.AgentPrompt);
            if (data.AllowsMultiple !== undefined) obj.AllowsMultiple(data.AllowsMultiple);
            if (data.AppearsOnTraffic !== undefined) obj.AppearsOnTraffic(data.AppearsOnTraffic);
            if (data.Currency !== undefined) obj.Currency(data.Currency);
            if (data.Description !== undefined) obj.Description(data.Description);
            if (data.DisplayOrder !== undefined) obj.DisplayOrder(data.DisplayOrder);
            if (data.IsMandatory !== undefined) obj.IsMandatory(data.IsMandatory);
            if (data.IsScopedByMainLocation !== undefined) obj.IsScopedByMainLocation(data.IsScopedByMainLocation);
            if (data.IsScopedByRelatedLocation !== undefined) obj.IsScopedByRelatedLocation(data.IsScopedByRelatedLocation);
            if (data.Title !== undefined) obj.Title(data.Title);
        });
    }
}



// WEBPACK FOOTER //
// ./server/models/Coordination.ts